import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { buildNavLinks, getListYourGearDestination } from './navLinks';

function getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount) {
  if (link.to === '/messaging') return messagingUnreadCount;
  if (link.to === '/my-bookings') return paymentDueCount;
  if (link.to === '/pending-rentals') return pendingRentalCount;
  return 0;
}

function getNavBadgeVariant(link) {
  if (link.to === '/my-bookings' || link.to === '/pending-rentals') return 'payment';
  return 'message';
}

function NavLinkWithBadge({ link, navLinkStyle, badgeCount = 0, badgeVariant = 'message', onClick }) {
  const showBadge = badgeCount > 0;

  return (
    <Link
      to={link.to}
      onClick={onClick}
      className={showBadge ? 'nav-link-with-badge' : undefined}
      style={{
        ...navLinkStyle,
        position: 'relative',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '4px',
      }}
    >
      <span>{link.label}</span>
      {showBadge && (
        <span
          className={`nav-unread-badge${badgeVariant === 'payment' ? ' payment-due-badge' : ''}`}
          aria-label={
            badgeVariant === 'payment'
              ? `${badgeCount} items need attention`
              : `${badgeCount} unread messages`
          }
        >
          {badgeCount > 9 ? '9+' : badgeCount}
        </span>
      )}
    </Link>
  );
}

function SiteHeader({
  user,
  isGearOwner = false,
  headerFirstName,
  canEditSite,
  isSiteOwner,
  isAdmin,
  isEnablingAdmin,
  messagingUnreadCount = 0,
  paymentDueCount = 0,
  pendingRentalCount = 0,
  onEnableSiteEditing,
  onSignOut,
  onOpenLogin,
}) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navLinks = useMemo(
    () => buildNavLinks({ user, isGearOwner }),
    [user, isGearOwner]
  );

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const navLinkStyle = {
    textDecoration: 'none',
    color: '#374151',
    fontWeight: '500',
  };

  const profileLinkStyle = {
    padding: '9px 20px',
    backgroundColor: '#f3f4f6',
    color: '#1f2937',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '0.95rem',
  };

  const listGearLinkStyle = {
    padding: '9px 18px',
    backgroundColor: '#059669',
    color: '#ffffff',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    fontSize: '0.95rem',
    whiteSpace: 'nowrap',
  };

  const listGearDestination = getListYourGearDestination(user);

  return (
    <header
      className="site-header"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        zIndex: 1000,
        padding: '18px 32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
      }}
    >
      <Link to="/" className="site-header-brand" aria-label="ATTACK! Your Adventure home">
        <img
          src={`${process.env.PUBLIC_URL}/logo192.png`}
          srcSet={`${process.env.PUBLIC_URL}/logo192.png 192w, ${process.env.PUBLIC_URL}/logo512.png 512w`}
          sizes="(max-width: 768px) 78px, 102px"
          alt="ATTACK! Your Adventure"
          className="site-header-logo"
        />
      </Link>

      <nav className="site-header-nav desktop-only" style={{ display: 'flex', gap: '28px', fontSize: '1.05rem' }}>
        {navLinks.map((link) => (
          <NavLinkWithBadge
            key={link.to}
            link={link}
            navLinkStyle={navLinkStyle}
            badgeCount={getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount)}
            badgeVariant={getNavBadgeVariant(link)}
          />
        ))}
        {isAdmin && (
          <Link to="/admin" style={{ ...navLinkStyle, color: '#2563eb', fontWeight: '700' }}>
            Admin
          </Link>
        )}
      </nav>

      <div className="site-header-actions desktop-only" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Link to={listGearDestination} className="site-header-list-gear" style={listGearLinkStyle}>
          List your gear
        </Link>
        {canEditSite && (
          <span style={{ fontSize: '0.85rem', color: '#2563eb', fontWeight: '600' }}>
            Admin edit mode
          </span>
        )}
        {isSiteOwner && !isAdmin && (
          <button
            onClick={onEnableSiteEditing}
            disabled={isEnablingAdmin}
            style={{
              padding: '9px 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isEnablingAdmin ? 'not-allowed' : 'pointer',
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
          >
            {isEnablingAdmin ? 'Enabling...' : 'Enable site editing'}
          </button>
        )}
        {user ? (
          <>
            <Link to="/profile" style={profileLinkStyle}>
              {headerFirstName}
            </Link>
            <button
              onClick={onSignOut}
              style={{
                padding: '9px 22px',
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Logout
            </button>
          </>
        ) : (
          <>
            <Link to="/create-profile" style={profileLinkStyle}>
              Create Account
            </Link>
            <button
              onClick={onOpenLogin}
              style={{
                padding: '9px 24px',
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
              }}
            >
              Login
            </button>
          </>
        )}
      </div>

      <button
        type="button"
        className="mobile-menu-button mobile-only"
        aria-label="Open menu"
        onClick={() => setMobileMenuOpen(true)}
      >
        ☰
      </button>

      {mobileMenuOpen && (
        <>
          <div className="mobile-nav-backdrop mobile-only" onClick={closeMobileMenu} />
          <nav className="mobile-nav-drawer mobile-only" aria-label="Mobile navigation">
            <h2>Menu</h2>
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`mobile-nav-link${getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount) > 0 ? ' mobile-nav-link-unread' : ''}`}
                onClick={closeMobileMenu}
              >
                <span>{link.label}</span>
                {getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount) > 0 && (
                  <span
                    className={`nav-unread-badge mobile-nav-unread-badge${getNavBadgeVariant(link) === 'payment' ? ' payment-due-badge' : ''}`}
                  >
                    {getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount) > 9
                      ? '9+'
                      : getNavBadgeCount(link, messagingUnreadCount, paymentDueCount, pendingRentalCount)}
                  </span>
                )}
              </Link>
            ))}
            {isAdmin && (
              <Link to="/admin" className="mobile-nav-link admin" onClick={closeMobileMenu}>
                Admin
              </Link>
            )}

            <Link
              to={listGearDestination}
              className="mobile-nav-button mobile-nav-list-gear"
              onClick={closeMobileMenu}
              style={{ ...listGearLinkStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              List your gear
            </Link>

            <div className="mobile-nav-actions">
              {canEditSite && (
                <span style={{ color: '#2563eb', fontWeight: '600', padding: '0 12px' }}>
                  Admin edit mode
                </span>
              )}
              {isSiteOwner && !isAdmin && (
                <button
                  type="button"
                  className="mobile-nav-button"
                  onClick={() => {
                    onEnableSiteEditing();
                    closeMobileMenu();
                  }}
                  disabled={isEnablingAdmin}
                  style={{ backgroundColor: '#2563eb', color: 'white' }}
                >
                  {isEnablingAdmin ? 'Enabling...' : 'Enable site editing'}
                </button>
              )}
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="mobile-nav-button"
                    onClick={closeMobileMenu}
                    style={{ ...profileLinkStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {headerFirstName}
                  </Link>
                  <button
                    type="button"
                    className="mobile-nav-button"
                    onClick={() => {
                      onSignOut();
                      closeMobileMenu();
                    }}
                    style={{ backgroundColor: '#ef4444', color: 'white' }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/create-profile"
                    className="mobile-nav-button"
                    onClick={closeMobileMenu}
                    style={{ ...profileLinkStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    Create Account
                  </Link>
                  <button
                    type="button"
                    className="mobile-nav-button"
                    onClick={() => {
                      onOpenLogin();
                      closeMobileMenu();
                    }}
                    style={{ backgroundColor: '#3b82f6', color: 'white' }}
                  >
                    Login
                  </button>
                </>
              )}
            </div>
          </nav>
        </>
      )}
    </header>
  );
}

export default SiteHeader;